import { 
  LayoutDashboard,
  Layers,
  MessageCircle,
  BarChart3,
  FileText,
  Users,
  Megaphone,
  Sparkles,
  MessagesSquare,
  MoreHorizontal,
  Shield,
  ChevronDown,
  Plus,
  Check,
  Moon,
  Sun
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/useProfileQuery";
import { useFeedbackBadges } from "@/hooks/useFeedbackBadges";
import { useTheme } from "next-themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useTour } from "@/contexts/TourContext";
import { Button } from "@/components/ui/button";
import { PlatformFeedbackDialog } from "@/components/PlatformFeedbackWidget";
import { AIChatPanel } from "@/components/ai-hub/AIChatPanel";
import { SubscriptionStatusBadge } from "@/components/sidebar/SubscriptionStatusBadge";
import { useState } from "react";

// Flat navigation items matching desktop sidebar exactly
const navigationItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Pages", url: "/dashboard/pages", icon: Layers, dataTour: "sidebar-pages" },
  { title: "Feedback", url: "/dashboard/feedback", icon: MessageCircle },
  { title: "Analytics", url: "/dashboard/analytics", icon: BarChart3 },
  { title: "Reports", url: "/dashboard/reports", icon: FileText },
  { title: "Manager View", url: "/dashboard/manager", icon: Users, requiresManager: true },
  { title: "Campaigns", url: "/dashboard/campaigns", icon: Megaphone },
  { title: "AI Insights", url: "/dashboard/ai-hub", icon: Sparkles, dataTour: "mobile-ai-insights" },
];

interface MobileSidebarContentProps {
  onClose?: () => void;
}

// Theme toggle menu item for mobile
function ThemeMenuItemMobile() {
  const { theme, setTheme } = useTheme();
  
  const cycleTheme = () => {
    if (theme === "light") setTheme("dark");
    else if (theme === "dark") setTheme("system");
    else setTheme("light");
  };
  
  const getThemeLabel = () => {
    if (theme === "dark") return "Dark";
    if (theme === "light") return "Light";
    return "System";
  };
  
  return (
    <DropdownMenuItem
      onClick={cycleTheme}
      className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent transition-colors text-sm flex items-center justify-between"
    >
      <span className="flex items-center gap-2">
        {theme === "dark" ? (
          <Moon className="h-4 w-4" />
        ) : (
          <Sun className="h-4 w-4" />
        )}
        Theme
      </span>
      <span className="text-muted-foreground text-xs">{getThemeLabel()}</span>
    </DropdownMenuItem>
  );
}

export function MobileSidebarContent({ onClose }: MobileSidebarContentProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useAdminAuth();
  const { startTour } = useTour();
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const { data: isManager = false } = useQuery({
    queryKey: ["is-manager", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data, error } = await supabase
        .from("user_manager_roles")
        .select("id")
        .eq("user_id", user.id)
        .limit(1);
      if (error) throw error;
      return data && data.length > 0;
    },
    enabled: !!user?.id,
  });

  const { data: profile } = useProfileQuery();
  const { getBadgeCount: getSharedBadgeCount } = useFeedbackBadges();

  const isActive = (url: string) => {
    if (url === "/dashboard/settings") {
      return location.pathname.startsWith("/dashboard/settings");
    }
    return location.pathname === url;
  };

  const handleNavClick = async (itemTitle: string) => {
    if (itemTitle === "Feedback") {
      await supabase
        .from("profiles")
        .update({ last_feedback_viewed_at: new Date().toISOString() })
        .eq("id", user?.id);
    } else if (itemTitle === "Pages") {
      await supabase
        .from("profiles")
        .update({ last_pages_viewed_at: new Date().toISOString() })
        .eq("id", user?.id);
    }
    onClose?.();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
    onClose?.();
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "S";
    return name.charAt(0).toUpperCase();
  };

  const getBadgeCount = (itemTitle: string) => {
    if (itemTitle === "Feedback") return getSharedBadgeCount("feedback");
    if (itemTitle === "Pages") return getSharedBadgeCount("pages");
    return null;
  };

  const companyName = profile?.company_name || "sendfeedback";
  const userEmail = user?.email || "";

  return (
    <div className="flex flex-col h-screen bg-sidebar">
      {/* Header - Company Selector + Status Badge */}
      <div className="px-3 py-4 flex-shrink-0 space-y-3">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 hover:bg-accent rounded-lg p-1.5 transition-colors w-full">
              <Avatar className="h-8 w-8 rounded-lg bg-primary">
                <AvatarImage src={profile?.logo_url || undefined} className="object-cover rounded-lg" />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium rounded-lg">
                  {getInitials(companyName)}
                </AvatarFallback>
              </Avatar>
              <span className="text-[13px] font-medium text-foreground truncate flex-1 text-left">
                {companyName.toLowerCase()}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 rounded-lg bg-popover z-[100] shadow-lg border border-border"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            {/* Current Company - Selected */}
            <DropdownMenuItem
              className="py-2 px-2 cursor-default rounded-md bg-accent/50 text-sm flex items-center gap-2"
            >
              <Avatar className="h-5 w-5 rounded-md bg-primary">
                <AvatarImage 
                  src={profile?.logo_url || undefined}
                  className="object-cover rounded-md"
                />
                <AvatarFallback className="rounded-md bg-primary text-primary-foreground text-[10px]">
                  {getInitials(companyName)}
                </AvatarFallback>
              </Avatar>
              <span className="flex-1 truncate">
                {companyName.toLowerCase()}
              </span>
              <Check className="h-4 w-4 text-primary" />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* Create New Company */}
            <DropdownMenuItem
              onClick={() => {
                navigate("/dashboard/new-company");
                onClose?.();
              }}
              className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent transition-colors text-sm flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create new company
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Subscription Status Badge */}
        <SubscriptionStatusBadge />
        
        {/* Direct Add Company Button for better mobile UX */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            navigate("/dashboard/new-company");
            onClose?.();
          }}
          className="w-full justify-start gap-2 text-[13px] text-muted-foreground hover:text-foreground mt-2 h-9"
        >
          <Plus className="h-4 w-4" />
          Add another company
        </Button>
      </div>

      {/* Navigation - Flat List */}
      <div className="flex-1 overflow-y-auto px-0 pt-0" data-tour="sidebar-navigation">
        <nav className="space-y-0.5">
          {navigationItems
            .filter(item => !item.requiresManager || isManager)
            .map((item) => {
              const badgeCount = getBadgeCount(item.title);
              const active = isActive(item.url);
              
              return (
                <NavLink
                  key={item.title}
                  to={item.url}
                  onClick={() => handleNavClick(item.title)}
                  data-tour={item.dataTour}
                  className={`flex items-center gap-3 px-4 mx-2 h-11 rounded-lg transition-colors ${
                    active
                      ? "bg-accent text-foreground"
                      : "text-foreground/80 hover:bg-accent"
                  }`}
                >
                  <item.icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-foreground' : 'text-muted-foreground'}`} strokeWidth={1.5} />
                  <span className="flex-1 text-[13px] font-medium">{item.title}</span>
                  {badgeCount && (
                    <Badge className="h-5 min-w-5 px-1.5 text-[10px] font-semibold" variant="destructive">
                      {badgeCount > 9 ? "9+" : badgeCount}
                    </Badge>
                  )}
                </NavLink>
              );
            })}

          {/* AI Assistant - opens chat panel */}
          <button
            onClick={() => setChatOpen(true)}
            data-tour="mobile-ai-assistant"
            className="flex items-center gap-3 px-4 mx-2 h-11 rounded-lg transition-colors text-foreground/80 hover:bg-accent w-[calc(100%-1rem)]"
          >
            <MessagesSquare className="h-[18px] w-[18px] shrink-0 text-muted-foreground" strokeWidth={1.5} />
            <span className="flex-1 text-[13px] font-medium text-left">AI Assistant</span>
          </button>

          {/* Admin Link - Fixed route to match desktop */}
          {isAdmin && (
            <NavLink
              to="/admins/dashboard"
              onClick={onClose}
              className={`flex items-center gap-3 px-4 mx-2 h-11 rounded-lg transition-colors ${
                location.pathname.startsWith("/admins")
                  ? "bg-accent text-foreground"
                  : "text-foreground/80 hover:bg-accent"
              }`}
            >
              <Shield className={`h-[18px] w-[18px] shrink-0 ${location.pathname.startsWith("/admins") ? 'text-foreground' : 'text-muted-foreground'}`} strokeWidth={1.5} />
              <span className="flex-1 text-[13px] font-medium">Admin</span>
            </NavLink>
          )}
        </nav>
      </div>

      {/* Footer - Email + More Menu */}
      <div className="flex-shrink-0 px-3 py-3 border-t border-border/40">
        <div className="flex items-center gap-3">
          <span className="flex-1 text-[13px] text-foreground/80 truncate">
            {userEmail}
          </span>
          
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" aria-label="Account options">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={() => {
                  navigate("/dashboard/settings");
                  onClose?.();
                }}
                className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent transition-colors text-sm"
              >
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  navigate("/dashboard/settings?tab=billing");
                  onClose?.();
                }}
                className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent transition-colors text-sm"
              >
                Billing
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => {
                  startTour();
                  onClose?.();
                }}
                className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent transition-colors text-sm"
              >
                Interactive Tour
              </DropdownMenuItem>
              <DropdownMenuItem 
                asChild
                className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent transition-colors text-sm"
              >
                <a href="https://docs.sendfeedback.ai" target="_blank" rel="noopener noreferrer">
                  Help & Support
                </a>
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      navigate("/admins/dashboard");
                      onClose?.();
                    }}
                    className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent transition-colors text-sm"
                  >
                    Admin Dashboard
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => setFeedbackDialogOpen(true)}
                className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent transition-colors text-sm"
              >
                Platform Feedback
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <ThemeMenuItemMobile />
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout} 
                className="py-2 px-2 cursor-pointer rounded-md hover:bg-accent text-destructive focus:text-destructive focus:bg-destructive/10 transition-colors text-sm"
              >
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Platform Feedback Dialog */}
      <PlatformFeedbackDialog 
        open={feedbackDialogOpen} 
        onOpenChange={setFeedbackDialogOpen} 
      />

      {/* AI Chat Panel */}
      <AIChatPanel open={chatOpen} onOpenChange={setChatOpen} />
    </div>
  );
}
